import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ArticleIcon from '@mui/icons-material/Article';
import BugReportIcon from '@mui/icons-material/BugReport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import { userApi, ApiError } from '../../../services/api';
import styles from './ProfileSupportScreen.module.css';

interface SupportOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  action: () => void;
  available?: boolean;
  badge?: string;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

const ProfileSupportScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [selectedFAQ, setSelectedFAQ] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const supportOptions: SupportOption[] = [
    {
      id: 'chat',
      title: 'Live Chat',
      description: 'Chat with our support team',
      icon: <ChatIcon />,
      action: () => handleLiveChat(),
      available: true,
      badge: 'Online'
    },
    {
      id: 'email',
      title: 'Email Support',
      description: 'support@mariohealth.com',
      icon: <EmailIcon />,
      action: () => window.location.href = 'mailto:support@mariohealth.com'
    },
    {
      id: 'phone',
      title: 'Phone Support',
      description: '1-800-MARIO-HELP',
      icon: <PhoneIcon />,
      action: () => window.location.href = 'tel:18006274643'
    },
    {
      id: 'docs',
      title: 'Help Center',
      description: 'Browse help articles',
      icon: <ArticleIcon />,
      action: () => window.open('https://help.mariohealth.com', '_blank')
    }
  ];

  const faqItems: FAQItem[] = [
    {
      id: 'faq1',
      question: 'How do I update my insurance information?',
      answer: 'Go to Profile → Contact Information and update your insurance details. You can also link employer accounts for automatic updates.'
    },
    {
      id: 'faq2',
      question: 'How are cost estimates calculated?',
      answer: 'We use your insurance plan details, provider contracts, and historical claims data to provide accurate cost estimates. Estimates include your deductible, copay, and coinsurance.'
    },
    {
      id: 'faq3',
      question: 'Can I see doctors outside my network?',
      answer: 'Yes, you can see out-of-network providers. Mario Health shows you both in-network and out-of-network costs so you can make informed decisions.'
    },
    {
      id: 'faq4',
      question: 'How do referral rewards work?',
      answer: 'When you invite friends and they complete their first appointment, both you and your friend receive $20 in credits. There\'s no limit to how many friends you can invite.'
    },
    {
      id: 'faq5',
      question: 'Is my health information secure?',
      answer: 'Yes, we use bank-level encryption and are HIPAA compliant. Your health information is never shared without your explicit consent.'
    }
  ];

  const handleLiveChat = () => {
    // In production, this would open a chat widget
    console.log('Opening live chat...');
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackType || !feedbackText.trim()) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const result = await userApi.sendFeedback({
        type: feedbackType,
        message: feedbackText
      });

      if (result.success) {
        setSubmitSuccess(true);
        setFeedbackText('');
        setFeedbackType(null);
        setTimeout(() => setSubmitSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.status, error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFAQ = (faqId: string) => {
    setSelectedFAQ(selectedFAQ === faqId ? null : faqId);
  };

  return (
    <div className={styles.profileSupport}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Support</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        {/* Contact Options */}
        <div className={styles.contactSection}>
          <h2 className={styles.sectionTitle}>Contact Us</h2>
          <div className={styles.optionsGrid}>
            {supportOptions.map(option => (
              <button
                key={option.id}
                className={styles.optionCard}
                onClick={option.action}
              >
                <div className={styles.optionIcon}>{option.icon}</div>
                <div className={styles.optionContent}>
                  <h3 className={styles.optionTitle}>
                    {option.title}
                    {option.badge && (
                      <span className={styles.optionBadge}>{option.badge}</span>
                    )}
                  </h3>
                  <p className={styles.optionDescription}>{option.description}</p>
                </div>
                <ChevronRightIcon className={styles.optionArrow} />
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className={styles.faqSection}>
          <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
          <div className={styles.faqList}>
            {faqItems.map(faq => (
              <div key={faq.id} className={styles.faqItem}>
                <button
                  className={styles.faqQuestion}
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <QuestionAnswerIcon className={styles.faqIcon} />
                  <span>{faq.question}</span>
                  <ChevronRightIcon 
                    className={`${styles.faqToggle} ${selectedFAQ === faq.id ? styles.faqToggleOpen : ''}`} 
                  />
                </button>
                {selectedFAQ === faq.id && (
                  <div className={styles.faqAnswer}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Feedback Section */}
        <div className={styles.feedbackSection}>
          <h2 className={styles.sectionTitle}>Send Feedback</h2>
          <div className={styles.feedbackForm}>
            <div className={styles.feedbackTypes}>
              <button
                className={`${styles.feedbackType} ${feedbackType === 'bug' ? styles.feedbackTypeActive : ''}`}
                onClick={() => setFeedbackType('bug')}
              >
                <BugReportIcon />
                Report a Bug
              </button>
              <button
                className={`${styles.feedbackType} ${feedbackType === 'feature' ? styles.feedbackTypeActive : ''}`}
                onClick={() => setFeedbackType('feature')}
              >
                <FeedbackIcon />
                Feature Request
              </button>
            </div>

            {feedbackType && (
              <>
                <textarea
                  className={styles.feedbackTextarea}
                  placeholder={feedbackType === 'bug' 
                    ? 'Please describe the issue you encountered...' 
                    : 'Tell us about the feature you\'d like to see...'}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  rows={5}
                />
                <button
                  className={styles.submitButton}
                  onClick={handleSubmitFeedback}
                  disabled={!feedbackText.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress size={20} color="inherit" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Feedback'
                  )}
                </button>
              </>
            )}

            {submitSuccess && (
              <div className={styles.successMessage}>
                <CheckCircleIcon className={styles.successIcon} />
                Thank you for your feedback! We'll review it shortly.
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className={styles.quickLinksSection}>
          <h2 className={styles.sectionTitle}>Quick Links</h2>
          <div className={styles.quickLinks}>
            <a href="https://mariohealth.com/privacy" target="_blank" rel="noopener noreferrer" className={styles.quickLink}>
              Privacy Policy
            </a>
            <a href="https://mariohealth.com/terms" target="_blank" rel="noopener noreferrer" className={styles.quickLink}>
              Terms of Service
            </a>
            <a href="https://mariohealth.com/hipaa" target="_blank" rel="noopener noreferrer" className={styles.quickLink}>
              HIPAA Notice
            </a>
            <a href="https://status.mariohealth.com" target="_blank" rel="noopener noreferrer" className={styles.quickLink}>
              System Status
            </a>
          </div>
        </div>

        {/* Support Hours */}
        <div className={styles.hoursInfo}>
          <HelpOutlineIcon className={styles.hoursIcon} />
          <div>
            <h3 className={styles.hoursTitle}>Support Hours</h3>
            <p className={styles.hoursText}>
              Live Chat: Mon-Fri 8AM-8PM EST, Sat-Sun 9AM-5PM EST<br />
              Phone Support: Mon-Fri 8AM-6PM EST<br />
              Email: We respond within 24 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSupportScreen;